import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Circle, Loader2, Pencil, Plus, RefreshCw, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import { deleteImpactText, instanceStatusKey } from '@/lib/instanceForm';
import type { Instance } from '@/types/instance';

interface InstanceListProps {
  instances: Instance[] | undefined;
  error: string | null;
  loading: boolean;
  refetch: () => void;
  onAdd: () => void;
  onEdit: (instance: Instance) => void;
}

const searchText = (i: Instance) => `${i.name} ${i.url}`;

const iconButton =
  'size-8 outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function InstanceList({ instances, error, loading, refetch, onAdd, onEdit }: InstanceListProps) {
  const syncingRef = useRef(new Set<number>());
  const [syncing, setSyncing] = useState<ReadonlySet<number>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<Instance | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    },
    [],
  );
  const deletingRef = useRef(false);
  const [deleting, setDeleting] = useState(false);

  const syncNow = async (i: Instance) => {
    if (!i.is_active || syncingRef.current.has(i.id)) return;
    syncingRef.current.add(i.id);
    setSyncing(new Set(syncingRef.current));
    try {
      await api.instances.triggerSync(i.id);
      toast.success('Sync started');
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(refetch, 2000);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to start sync');
    } finally {
      syncingRef.current.delete(i.id);
      setSyncing(new Set(syncingRef.current));
      refetch();
    }
  };

  const confirmDelete = async () => {
    const target = deleteTarget;
    if (!target || !deleteOpen || deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await api.instances.delete(target.id);
      toast.success('Instance deleted');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to delete instance');
    } finally {
      deletingRef.current = false;
      setDeleting(false);
      setDeleteOpen(false);
      refetch();
    }
  };

  const columns: Column<Instance>[] = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (i) => i.name.toLowerCase(),
      className: 'max-w-56',
      cell: (i) => (
        <div className="truncate font-medium" title={i.name}>
          {i.name}
        </div>
      ),
    },
    {
      key: 'url',
      header: 'URL',
      className: 'max-w-64',
      cell: (i) => (
        <div className="truncate font-mono text-xs" title={i.url}>
          {i.url}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Sync status',
      className: 'max-w-64',
      cell: (i) => {
        const key = instanceStatusKey(i);
        return (
          <div className="min-w-0 space-y-1">
            <StatusBadge status={key} />
            {key === 'failed' && i.last_sync_error ? (
              <div className="truncate text-xs text-status-danger" title={i.last_sync_error}>
                <span className="sr-only">Sync error: </span>
                {i.last_sync_error}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'last_sync',
      header: 'Last sync',
      sortValue: (i) => {
        if (!i.last_sync_at) return null;
        const t = Date.parse(i.last_sync_at);
        return Number.isNaN(t) ? null : t;
      },
      cell: (i) => {
        const t = i.last_sync_at ? Date.parse(i.last_sync_at) : NaN;
        if (Number.isNaN(t)) return <span className="text-muted-foreground">Never</span>;
        return (
          <time dateTime={new Date(t).toISOString()} title={new Date(t).toLocaleString()} className="whitespace-nowrap text-muted-foreground">
            {formatAgo(t)}
          </time>
        );
      },
    },
    {
      key: 'tls',
      header: 'TLS',
      cell: (i) =>
        i.skip_tls_verify ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-status-warning">
            <ShieldAlert className="size-4" aria-hidden />
            Verification skipped
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm">
            <ShieldCheck className="size-4 text-muted-foreground" aria-hidden />
            Verified
          </span>
        ),
    },
    {
      key: 'active',
      header: 'Active',
      cell: (i) =>
        i.is_active ? (
          <span className="inline-flex items-center gap-1 text-sm">
            <CheckCircle2 className="size-4 text-status-success" aria-hidden />
            Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <Circle className="size-4" aria-hidden />
            Inactive
          </span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      cell: (i) => {
        const busy = syncing.has(i.id);
        const unavailable = !i.is_active;
        return (
          <div className="flex justify-end gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={`${iconButton}${unavailable ? ' opacity-50' : ''}`}
              aria-label={`Sync ${i.name} now`}
              aria-disabled={unavailable || busy}
              aria-busy={busy || undefined}
              title={unavailable ? 'Inactive instances aren\'t synced. Activate it first.' : 'Sync now'}
              onClick={() => void syncNow(i)}
            >
              {busy ? <Loader2 className="animate-spin" aria-hidden /> : <RefreshCw aria-hidden />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={iconButton}
              aria-label={`Edit ${i.name}`}
              title="Edit"
              onClick={() => onEdit(i)}
            >
              <Pencil aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={`${iconButton} text-destructive hover:text-destructive`}
              aria-label={`Delete ${i.name}`}
              title="Delete"
              onClick={() => {
                setDeleteTarget(i);
                setDeleteOpen(true);
              }}
            >
              <Trash2 aria-hidden />
            </Button>
          </div>
        );
      },
    },
  ];

  const hasData = instances !== undefined;

  return (
    <div className="space-y-3" data-focus-fallback tabIndex={-1}>
      {hasData && error !== null && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known data — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={refetch}>
            Retry
          </Button>
        </div>
      )}
      <DataTable<Instance>
        columns={columns}
        rows={instances}
        getRowKey={(i) => i.id}
        searchText={searchText}
        searchPlaceholder="Search instances…"
        loading={loading}
        error={hasData ? null : error}
        onRetry={refetch}
        empty={{
          title: 'No instances yet',
          description: 'Add a Portainer instance to start syncing endpoints.',
          action: (
            <Button size="sm" onClick={onAdd}>
              <Plus aria-hidden />
              Add instance
            </Button>
          ),
        }}
        pageSize={20}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(o) => {
          if (!o && !deleting) {
            setDeleteOpen(false);
          }
        }}
        title="Delete instance?"
        description={deleteTarget ? deleteImpactText(deleteTarget) : undefined}
        confirmLabel="Delete instance"
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
